import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { ScreenshotFrame } from "@/components/marketing/ScreenshotFrame";
import DonateDialog from "@/components/DonateDialog";
import LandingReviews from "@/components/LandingReviews";
import LandingLaunched from "@/components/LandingLaunched";
import { AUDIOBOOKS_ENABLED } from "@/config/features";
import {
  ArrowRight, Sparkles, PenSquare, Library, LayoutList, Boxes, Waypoints,
  Upload, Image as ImageIcon, Download, SpellCheck2, Feather, HeartHandshake,
  ShieldCheck, BookMarked, ExternalLink, MonitorSmartphone, Headphones,
} from "lucide-react";

const rise = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const FEATURES = [
  {
    id: "manuscript",
    eyebrow: "The Manuscript",
    icon: PenSquare,
    title: "Write like a novelist. Remember like a machine.",
    body: "A distraction-free, Word-class editor built for long-form fiction. As you write, CodexStudio quietly recognises your characters, weapons, places and objects — colour-coding each one right in the prose. Click any highlight to edit that detail, and the change echoes across every chapter.",
    bullets: ["Colour-coded vital details inline", "Click-to-edit updates everywhere", "Autosave, word count, reading time"],
    img: "/marketing/manuscript.png",
    label: "codexstudio · manuscript",
  },
  {
    id: "codex",
    eyebrow: "The Storyboard Codex",
    icon: Library,
    title: "Every vital detail, in one searchable board.",
    body: "Your story's memory lives here. Characters, factions, locations and artefacts become cards you can search, filter by type, and mark as 'included'. No more scrolling three chapters back to remember the name of that rifle.",
    bullets: ["Search & filter by type", "Mark details as included", "AI rates what actually matters"],
    img: "/marketing/codex.png",
    label: "codexstudio · storyboard codex",
    flip: true,
  },
  {
    id: "blueprint",
    eyebrow: "The Story Blueprint",
    icon: LayoutList,
    title: "Plan big-to-small — logline to acts to chapters.",
    body: "Start with a single logline, break it into acts, and drop chapters onto a corkboard you can drag and reshape. Tension sliders, status flags and an AI 'Snowflake' expand help you outline before you write a single scene.",
    bullets: ["Corkboard & outline views", "Drag chapters between acts", "Detail-plan chapters with a beat/scene checklist", "AI expands logline → acts → chapters"],
    img: "/marketing/blueprint.png",
    label: "codexstudio · story blueprint",
  },
  {
    id: "loadouts",
    eyebrow: "Gear Sync Continuity",
    icon: Boxes,
    title: "Never lose track of a single round — or arrow — again.",
    body: "A timeline-aware loadout engine for action, survival, fantasy and historical fiction. Tell it 'Rourke fired thirty rounds' or 'Mara loosed a dozen arrows' — it drains the right ammo pool, restocks from your source, and warns you the moment continuity breaks. It reads per-weapon calibres and magazine sizes, reconciles absolute ammo-checks ('down to two mags'), and when a character falls, flags them KIA so you can loot their gear and redistribute ammo across the squad. Carry Kits too — Med Kits and pouches whose contents live in your Codex and auto-fill each character's tick-off checklist.",
    bullets: ["Natural-language gear updates & smart ammo depletion", "Per-weapon calibres, magazine sizes & absolute ammo-checks", "KIA detection → loot the fallen & redistribute ammo", "Kit checklists, low-stock & continuity warnings"],
    img: "/marketing/loadouts.png",
    label: "codexstudio · loadouts",
    flip: true,
  },
];

const EXTRAS = [
  ...(AUDIOBOOKS_ENABLED ? [[Headphones, "AI Audiobook narration", "Turn any chapter — or your whole book — into natural, studio-quality narration in six voices, then download the MP3."]] : []),
  [Sparkles, "AI Detail Scan", "Sweep any chapter and let AI surface the details worth remembering."],
  [SpellCheck2, "AI Proofread", "Catch spelling and grammar slips without leaving your manuscript."],
  [ShieldCheck, "Multi-device safe sync", "Auto-refresh, save-guard and version history keep your work safe across every device."],
  [ExternalLink, "Dual-monitor panels", "Pop the Codex, Loadouts and Blueprint out into their own windows — edits sync live."],
  [BookMarked, "Section templates", "Reusable Prologue, Epilogue & About the Author sections, with a saved author bio."],
  [LayoutList, "Auto chapter numbering", "Chapters renumber themselves as you add, reorder or delete — custom titles kept."],
  [Boxes, "Kits & pouches", "Build a Med Kit in your Codex once — it auto-fills every character's loadout checklist."],
  [Upload, "Import your work", "Drop in a .docx, .txt or .pdf — it splits into chapters and extracts details."],
  [Download, "Export to PDF & Word", "Export a clean manuscript, or take your codex with you, whenever you like."],
  [Waypoints, "Branching / CYOA", "Build choose-your-own-adventure books with a visual branch map."],
  [ImageIcon, "Cover art", "Upload a cover for every project and make your desk feel like a shelf."],
  [MonitorSmartphone, "Install as an app", "Add CodexStudio to your home screen or desktop — it works like a native app."],
];

const STEPS = [
  ["Create your book", "Start a project or import an existing manuscript to break into chapters."],
  ["Write & auto-tag", "Draft in the editor while CodexStudio highlights and remembers your details."],
  ["Plan & keep continuity", "Outline on the Blueprint and let Gear Sync guard your story's logic."],
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [donateOpen, setDonateOpen] = useState(false);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <MarketingNav>
        <button onClick={() => scrollTo("features")} className="hover:text-foreground transition-colors" data-testid="nav-features">Features</button>
        <button onClick={() => scrollTo("how")} className="hover:text-foreground transition-colors" data-testid="nav-how">How it works</button>
        {AUDIOBOOKS_ENABLED && <Link to="/discover" className="hover:text-foreground transition-colors" data-testid="nav-discover">Audiobooks</Link>}
        <Link to="/guide" className="hover:text-foreground transition-colors" data-testid="nav-guide">Guide</Link>
        <button onClick={() => scrollTo("support")} data-testid="nav-support"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors px-3.5 py-1.5 font-semibold">
          <HeartHandshake className="w-4 h-4" /> Donate
        </button>
      </MarketingNav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1715304617193-a4f86af446bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1900"
            alt=""
            className="w-full h-full object-cover opacity-[0.14]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        </div>

        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...rise}>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-4 py-1.5 text-xs uppercase tracking-widest font-semibold text-primary">
              <Sparkles className="w-3.5 h-3.5" /> The writing studio that remembers
            </span>
          </motion.div>

          <motion.h1
            {...rise}
            transition={{ ...rise.transition, delay: 0.05 }}
            className="font-display font-bold tracking-tight leading-[1.05] mt-7 text-5xl sm:text-6xl lg:text-7xl max-w-4xl mx-auto"
          >
            Where your story keeps
            <span className="text-primary"> its own memory.</span>
          </motion.h1>

          <motion.p
            {...rise}
            transition={{ ...rise.transition, delay: 0.12 }}
            className="font-manuscript text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mt-6 leading-relaxed"
          >
            Write your novel in a beautiful editor while CodexStudio auto-tags every
            character, weapon and world you invent — and guards your continuity while you're at it.
          </motion.p>

          <motion.div
            {...rise}
            transition={{ ...rise.transition, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-9"
          >
            <Button data-testid="hero-cta-start" size="lg" onClick={() => navigate(user ? "/app" : "/auth")}
              className="h-12 px-7 text-base font-semibold gap-2 w-full sm:w-auto">
              {user ? "Open your studio" : "Start writing — it's free"} <ArrowRight className="w-4.5 h-4.5" />
            </Button>
            <Button data-testid="hero-cta-guide" size="lg" variant="outline" onClick={() => navigate("/guide")}
              className="h-12 px-7 text-base font-semibold w-full sm:w-auto">
              See how it works
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 max-w-5xl mx-auto"
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-primary/20 blur-3xl -z-10 rounded-full" />
              <ScreenshotFrame
                testid="hero-screenshot"
                src="/marketing/manuscript.png"
                alt="CodexStudio manuscript editor with colour-coded story details"
                label="codexstudio · manuscript"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature sections */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16 space-y-24 sm:space-y-36">
        {FEATURES.map((f) => (
          <div key={f.id} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div {...rise} className={f.flip ? "lg:order-2" : ""}>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-primary mb-4">
                <f.icon className="w-4 h-4" /> {f.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                {f.title}
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg mt-5 leading-relaxed">{f.body}</p>
              <ul className="mt-6 space-y-3">
                {f.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm sm:text-base">
                    <span className="mt-1 w-5 h-5 rounded-full bg-primary/15 grid place-items-center shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    </span>
                    <span className="text-foreground/90">{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...rise} transition={{ ...rise.transition, delay: 0.1 }} className={f.flip ? "lg:order-1" : ""}>
              <ScreenshotFrame testid={`feature-shot-${f.id}`} src={f.img} alt={f.title} label={f.label} />
            </motion.div>
          </div>
        ))}
      </section>

      {/* Extras grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <motion.div {...rise} className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Everything a serious storyteller needs
          </h2>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg">
            One studio, from first spark to finished draft.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {EXTRAS.map(([Icon, title, body], i) => (
            <motion.div
              key={title}
              {...rise}
              transition={{ ...rise.transition, delay: (i % 4) * 0.06 }}
              data-testid={`extra-${i}`}
              className="rounded-2xl border border-border bg-card p-6 hover:border-primary/50 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/12 grid place-items-center mb-4">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display text-xl font-bold mb-1.5">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-border bg-secondary/25">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <motion.div {...rise} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest font-semibold text-primary">How it works</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
              Up and writing in minutes
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map(([title, body], i) => (
              <motion.div key={title} {...rise} transition={{ ...rise.transition, delay: i * 0.08 }} data-testid={`step-${i}`}>
                <div className="font-display text-5xl font-bold text-primary/30 mb-3">0{i + 1}</div>
                <h3 className="font-display text-2xl font-bold mb-2">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button data-testid="how-guide-link" variant="outline" size="lg" onClick={() => navigate("/guide")} className="gap-2 font-semibold">
              Read the full guide <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* From the maker */}
      <section id="support" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.div
          {...rise}
          data-testid="founder-story"
          className="relative rounded-3xl border border-border bg-card/60 p-8 sm:p-12 overflow-hidden"
        >
          <div className="absolute -top-6 left-8 text-primary/15 font-display text-[9rem] leading-none select-none pointer-events-none">“</div>
          <div className="relative">
            <span className="text-xs uppercase tracking-widest font-semibold text-primary">Why I built this</span>
            <p className="font-manuscript text-2xl sm:text-3xl leading-snug tracking-tight mt-5 text-foreground">
              I'm an aspiring author who never actually finished a book — I kept drowning in
              my own notes, characters and world details. So I built the tool I wished I had.
              <span className="text-primary"> This is CodexStudio.</span>
            </p>
            <div className="flex items-center gap-3 mt-8">
              <div className="w-11 h-11 rounded-full bg-primary grid place-items-center shrink-0">
                <Feather className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <div className="font-display font-bold text-base">Philip Isa-Rock</div>
                <div className="text-sm text-muted-foreground">Maker of CodexStudio</div>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-sm text-muted-foreground flex-1">
                CodexStudio is free to use. If it's helping your story, a small donation keeps it going.
              </p>
              <Button onClick={() => setDonateOpen(true)} data-testid="landing-donate-button" className="gap-2 shrink-0">
                <HeartHandshake className="w-4 h-4" /> Support CodexStudio
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {AUDIOBOOKS_ENABLED && <LandingLaunched />}
      {AUDIOBOOKS_ENABLED && <LandingReviews />}

      {/* Final CTA */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...rise}>
            <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-8">
              <Feather className="w-8 h-8 text-primary-foreground" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Your saga is waiting to be remembered.
            </h2>
            <p className="text-muted-foreground text-lg mt-5 max-w-xl mx-auto">
              Create a free account and give your story a memory of its own.
            </p>
            <Button data-testid="final-cta" size="lg" onClick={() => navigate(user ? "/app" : "/auth")}
              className="h-12 px-8 text-base font-semibold gap-2 mt-9">
              {user ? "Open your studio" : "Begin your saga"} <ArrowRight className="w-4.5 h-4.5" />
            </Button>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
      <DonateDialog open={donateOpen} onOpenChange={setDonateOpen} />
    </div>
  );
}
